import { FORMATS } from "../printProducts";
import type { PrintEdition, PrintFormat } from "../printProducts";
import type { PromiseAsk } from "./promiseStore";

// What the promise modal says. Nassos's rules (2026-10-15): a pre-order is a promise to take the
// print with your next order once it is on sale; quantities change freely; removing one is final,
// you never get the chance to buy it again. Trading cards are gathered, one per day, and pre-ordered
// as a set from 21. The same words are on /terms under "Pre-orders".

const NOTHING_PAID = "nothing is paid now.";

function printName(format: PrintFormat, edition: PrintEdition): string {
  const name = FORMATS.find((f) => f.format === format)?.name ?? format;
  return edition === "original" ? `original ${name}` : name;
}

export function preorderAsk(number: number, format: PrintFormat, edition: PrintEdition): PromiseAsk {
  const what = `the #${number} ${printName(format, edition)}`;
  if (edition === "original") {
    return {
      title: `claim ${what}?`,
      body: [
        `claiming it is a promise: once prints go on sale, you take it with your next order. ${NOTHING_PAID}`,
        "letting it go later is final: nobody can claim it again.",
      ],
      confirm: "CLAIM IT",
      dismiss: "not now",
    };
  }
  return {
    title: `pre-order ${what}?`,
    body: [
      `a pre-order is a promise: once prints go on sale, you take it with your next order. ${NOTHING_PAID}`,
      "you can change how many any time. removing it is final: you can't pre-order or buy this print again.",
    ],
    confirm: "PRE-ORDER",
    dismiss: "not now",
  };
}

export function gatherAsk(number: number, edition: PrintEdition, needed: number): PromiseAsk {
  const original = edition === "original";
  return {
    title: original ? `claim the original #${number} trading card?` : `gather the #${number} trading card?`,
    body: [
      `${original ? "it counts as one of your gathered cards. " : ""}gathering is a promise, like a pre-order: at ${needed} cards you pre-order them as a set, and you take it with your next order once cards are on sale. ${NOTHING_PAID}`,
      original
        ? "letting it go later is final: nobody can claim it again."
        : "putting a card back is final: you can't gather or buy this day's card again.",
    ],
    confirm: original ? "CLAIM IT" : "GATHER",
    dismiss: "not now",
  };
}

export function setAsk(count: number): PromiseAsk {
  return {
    title: `pre-order your ${count} cards as a set?`,
    body: [
      `the set goes into the next print run, and you take it with your next order once cards are on sale. ${NOTHING_PAID}`,
      `removing the set later is final: you can't gather or buy any of its ${count} days again.`,
    ],
    confirm: "PRE-ORDER THE SET",
    dismiss: "not yet",
  };
}

export function removeAsk(number: number, format: PrintFormat, edition: PrintEdition): PromiseAsk {
  const what = `the #${number} ${printName(format, edition)}`;
  if (edition === "original") {
    return {
      title: `let ${what} go?`,
      body: ["this is final: nobody will be able to claim it again, you included."],
      confirm: "LET IT GO",
      dismiss: "keep it",
      final: true,
    };
  }
  if (format === "trading-card") {
    return {
      title: `put back ${what}?`,
      body: ["this is final: you'll never be able to gather or buy this day's card again."],
      confirm: "PUT IT BACK",
      dismiss: "keep it",
      final: true,
    };
  }
  return {
    title: `remove ${what}?`,
    body: [`this is final: you'll never be able to pre-order or buy ${what} again.`],
    confirm: "REMOVE",
    dismiss: "keep it",
    final: true,
  };
}

export function removeSetAsk(count: number): PromiseAsk {
  return {
    title: `remove your set of ${count} cards?`,
    body: [`this is final: you'll never be able to gather or buy any of its ${count} days again.`],
    confirm: "REMOVE THE SET",
    dismiss: "keep it",
    final: true,
  };
}
